import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Broker, OwnerContact, Property } from '../App';
import PropertyMapView from './PropertyMapView';
import './Properties.css';

interface PropertiesProps {
  properties: Property[];
  onDeleteProperty?: (id: string) => Promise<void>;
  onDeleteProperties?: (ids: string[]) => Promise<void>;
  onUpdateProperty?: (property: Property) => void;
  readOnly?: boolean;
  useServerFiltering?: boolean;
  brokerIdentity?: Pick<Broker, 'id' | 'name'> | null;
  enableAddressLinks?: boolean;
  headerAction?: React.ReactNode;
}

type FieldDef = {
  path: string;
  label: string;
  multiline?: boolean;
};

type SectionDef = {
  id: string;
  title: string;
  openByDefault?: boolean;
  fields: FieldDef[];
};

function getValueByPath(obj: any, path: string) {
  const parts = path.split('.');
  let cur = obj;
  for (const p of parts) {
    if (cur === null || cur === undefined) return '';
    cur = cur[p];
  }
  if (cur === null || cur === undefined) return '';
  if (typeof cur === 'string' || typeof cur === 'number' || typeof cur === 'boolean') return String(cur);
  try {
    return JSON.stringify(cur, null, 2);
  } catch {
    return String(cur);
  }
}

function setValueByPath(obj: any, path: string, value: any) {
  const parts = path.split('.');
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    if (!cur[p] || typeof cur[p] !== 'object') cur[p] = {};
    cur = cur[p];
  }
  cur[parts[parts.length - 1]] = value;
}

function isLikelyJsonText(s: string) {
  const t = s.trim();
  return t.startsWith('{') || t.startsWith('[');
}

function formatMarketValue(value: string) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const numeric = Number(raw.replace(/[$,]/g, ''));
  if (!Number.isFinite(numeric)) return raw;
  return numeric.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatUsPhone(value: string) {
  const digits = String(value ?? '').replace(/\D/g, '').slice(0, 10);
  if (!digits) return '';
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function createEmptyOwnerContact(): OwnerContact {
  return { firstName: '', lastName: '', age: '', cell: '', additionalPhones: [''], email: '', address: '' };
}

function normalizeOwnerContacts(ownerContacts: unknown): OwnerContact[] {
  if (!Array.isArray(ownerContacts)) return [];
  return ownerContacts.map((contact) => {
    const c = contact && typeof contact === 'object' ? (contact as OwnerContact) : {};
    const additionalPhones = Array.isArray(c.additionalPhones)
      ? c.additionalPhones.map((phone) => String(phone ?? ''))
      : [];
    return {
      firstName: String(c.firstName ?? ''),
      lastName: String(c.lastName ?? ''),
      age: String(c.age ?? ''),
      cell: String(c.cell ?? ''),
      additionalPhones: additionalPhones.length > 0 ? additionalPhones : [''],
      email: String(c.email ?? ''),
      address: String(c.address ?? ''),
    };
  });
}

function normalizePropertyForEditing(property: Property): Property {
  return {
    ...property,
    notes: typeof property.notes === 'string' ? property.notes : '',
    ownerContacts: normalizeOwnerContacts(property.ownerContacts),
  };
}

function getGoogleMapsUrl(property: Property): string {
  const full = property.fullAddress?.trim() || '';
  const parts = [property.address, property.city, property.state, property.zipcode].filter(Boolean).join(', ');
  const query = full || parts || '';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

const Properties: React.FC<PropertiesProps> = ({
  properties,
  onDeleteProperty: onDeletePropertyProp,
  onDeleteProperties: onDeletePropertiesProp,
  onUpdateProperty: onUpdatePropertyProp,
  readOnly = false,
  useServerFiltering = true,
  brokerIdentity,
  enableAddressLinks = false,
  headerAction,
}) => {
  const handleDeleteProperty = onDeletePropertyProp || (async () => {});
  const handleDeleteProperties = onDeletePropertiesProp || (async () => {});
  const handleUpdateProperty = onUpdatePropertyProp || (() => {});

  const PAGE_SIZE = 200;
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'map'>('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tenantFilter, setTenantFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [starredFilter, setStarredFilter] = useState<'all' | 'starred' | 'nonstarred'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [hasMoreFiltered, setHasMoreFiltered] = useState(false);
  const [tenantSuggestions, setTenantSuggestions] = useState<string[]>([]);
  const [citySuggestions, setCitySuggestions] = useState<string[]>([]);
  const [stateSuggestions, setStateSuggestions] = useState<string[]>([]);
  const [listSortKey, setListSortKey] = useState<'owner' | 'phone' | null>(null);
  const [listSortDirection, setListSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<Property | null>(null);
  const [isNotesEditing, setIsNotesEditing] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [isOwnerContactsEditing, setIsOwnerContactsEditing] = useState(false);
  const [ownerContactsDraft, setOwnerContactsDraft] = useState<OwnerContact[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshingFilteredReportAll, setIsRefreshingFilteredReportAll] = useState(false);
  const [focusMapPropertyId, setFocusMapPropertyId] = useState<string | null>(null);
  const [brokerNoteDraft, setBrokerNoteDraft] = useState('');
  const [brokerContactDraft, setBrokerContactDraft] = useState<OwnerContact>(createEmptyOwnerContact());
  const filterControlsRef = useRef<HTMLDivElement | null>(null);

  const getDisplayAddress = (property: Property) => property.address?.trim() || 'N/A';

  const getDisplayPropertyOwner = (property: Property) => {
    const contacts = normalizeOwnerContacts(property.ownerContacts);
    for (const c of contacts) {
      const name = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
      if (name) return name;
    }
    const listOwner = property.reportAllOwner;
    if (typeof listOwner === 'string' && listOwner.trim().length > 0) return listOwner.trim();
    const owner = property.reportAllParcel?.owner;
    return typeof owner === 'string' && owner.trim().length > 0 ? owner.trim() : 'N/A';
  };

  const getDisplayPhone = (property: Property) => {
    const contacts = normalizeOwnerContacts(property.ownerContacts);
    for (const c of contacts) {
      const cell = c.cell?.trim();
      if (cell) return formatUsPhone(cell);
    }
    return property.phone?.trim() || 'N/A';
  };

  const isStarredProperty = (property: Property) => !!property.starred;

  const toggleStarProperty = async (property: Property) => {
    if (readOnly) return;
    try {
      const resp = await fetch(`/api/properties/${property.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ starred: !property.starred }),
      });
      if (!resp.ok) return;
      const updated = (await resp.json()) as Property;
      handleUpdateProperty(updated);
      setFilteredProperties((prev) =>
        prev.map((p) => (p.id === updated.id ? { ...p, starred: updated.starred } : p))
      );
      if (selectedProperty?.id === updated.id) {
        setSelectedProperty((prev) => (prev ? { ...prev, starred: updated.starred } : prev));
      }
    } catch (e) {
      console.error('Error toggling star:', e);
    }
  };

  const handleListSort = (key: 'owner' | 'phone') => {
    if (listSortKey === key) {
      setListSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setListSortKey(key);
      setListSortDirection('asc');
    }
  };

  const getDisplayFullAddress = (property: Property) => {
    const full = property.fullAddress?.trim();
    if (full) return full;
    const addr = property.address?.trim();
    const city = property.city?.trim();
    const state = property.state?.trim();
    const zip = property.zipcode?.trim();
    const parts = [addr, [city, state, zip].filter(Boolean).join(' ').trim()].filter(
      (p) => typeof p === 'string' && p.trim().length > 0
    );
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  };

  const getPrimaryLabel = (property: Property) =>
    property.tenant?.trim() || property.address?.trim() || property.fullAddress?.trim() || 'N/A';

  const activeFilterCount = [tenantFilter, cityFilter, stateFilter].filter((v) => v.trim().length > 0).length +
    (starredFilter !== 'all' ? 1 : 0);
  const filteredPropertyIds = useMemo(() => filteredProperties.map((p) => p.id), [filteredProperties]);
  const filteredSelectedCount = filteredPropertyIds.filter((id) => selectedIds.includes(id)).length;
  const allFilteredSelected = filteredPropertyIds.length > 0 && filteredSelectedCount === filteredPropertyIds.length;
  const pageStart = filteredTotal === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const pageEnd = filteredTotal === 0 ? 0 : Math.min(filteredTotal, (currentPage - 1) * PAGE_SIZE + filteredProperties.length);

  const sortedFilteredProperties = useMemo(() => {
    if (!listSortKey) return filteredProperties;
    const sorted = [...filteredProperties];
    sorted.sort((a, b) => {
      let valA = '';
      let valB = '';
      if (listSortKey === 'owner') {
        valA = getDisplayPropertyOwner(a).toLowerCase();
        valB = getDisplayPropertyOwner(b).toLowerCase();
      } else {
        valA = getDisplayPhone(a).toLowerCase();
        valB = getDisplayPhone(b).toLowerCase();
      }
      const cmp = valA.localeCompare(valB);
      return listSortDirection === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [filteredProperties, listSortKey, listSortDirection]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, tenantFilter, cityFilter, stateFilter, starredFilter]);

  useEffect(() => {
    if (!useServerFiltering) {
      const q = searchTerm.trim().toLowerCase();
      const tenant = tenantFilter.trim().toLowerCase();
      const city = cityFilter.trim().toLowerCase();
      const state = stateFilter.trim().toLowerCase();

      let results = properties;
      if (q) results = results.filter((p) => [p.tenant, p.address, p.fullAddress, p.city, p.state, p.phone].filter(Boolean).join(' ').toLowerCase().includes(q));
      if (tenant) results = results.filter((p) => (p.tenant || '').toLowerCase().includes(tenant));
      if (city) results = results.filter((p) => (p.city || '').toLowerCase().includes(city));
      if (state) results = results.filter((p) => (p.state || '').toLowerCase().includes(state));
      if (starredFilter === 'starred') results = results.filter((p) => p.starred);
      if (starredFilter === 'nonstarred') results = results.filter((p) => !p.starred);

      const total = results.length;
      const offset = (currentPage - 1) * PAGE_SIZE;
      const page = results.slice(offset, offset + PAGE_SIZE);
      setFilteredProperties(page);
      setFilteredTotal(total);
      setTotalPages(total > 0 ? Math.ceil(total / PAGE_SIZE) : 0);
      setHasMoreFiltered(total > offset + page.length);
      return;
    }

    const controller = new AbortController();
    const fetchFilteredProperties = async () => {
      try {
        const params = new URLSearchParams();
        if (searchTerm.trim()) params.set('q', searchTerm.trim());
        if (tenantFilter.trim()) params.set('tenant', tenantFilter.trim());
        if (cityFilter.trim()) params.set('city', cityFilter.trim());
        if (stateFilter.trim()) params.set('state', stateFilter.trim());
        if (starredFilter !== 'all') params.set('starred', starredFilter);
        params.set('limit', String(PAGE_SIZE));
        params.set('page', String(currentPage));

        const response = await fetch(`/api/properties/search?${params.toString()}`, { signal: controller.signal });
        if (!response.ok) return;
        const data = await response.json();
        if (controller.signal.aborted) return;
        const total = Number.isFinite(data.total) ? data.total : 0;
        const serverTotalPages = Number.isFinite(data.totalPages) ? Math.max(0, data.totalPages) : (total > 0 ? Math.ceil(total / PAGE_SIZE) : 0);
        if (serverTotalPages > 0 && currentPage > serverTotalPages) {
          setCurrentPage(serverTotalPages);
          return;
        }
        setFilteredProperties(Array.isArray(data.properties) ? data.properties : []);
        setFilteredTotal(total);
        setTotalPages(serverTotalPages);
        setHasMoreFiltered(!!data.hasMore);
      } catch (error: any) {
        if (error?.name !== 'AbortError') console.error('Error fetching filtered properties:', error);
      }
    };
    fetchFilteredProperties();
    return () => controller.abort();
  }, [searchTerm, tenantFilter, cityFilter, stateFilter, starredFilter, properties, currentPage, useServerFiltering]);

  useEffect(() => {
    if (!isFilterOpen) return;

    if (!useServerFiltering) {
      const tenants = new Set<string>();
      const cities = new Set<string>();
      const states = new Set<string>();
      for (const p of properties) {
        if (p.tenant?.trim()) tenants.add(p.tenant.trim());
        if (p.city?.trim()) cities.add(p.city.trim());
        if (p.state?.trim()) states.add(p.state.trim());
      }
      setTenantSuggestions(Array.from(tenants).sort());
      setCitySuggestions(Array.from(cities).sort());
      setStateSuggestions(Array.from(states).sort());
      return;
    }

    const controller = new AbortController();
    const fetchDistinctValues = async (field: 'tenant' | 'city' | 'state', prefix: string, setter: (values: string[]) => void) => {
      try {
        const params = new URLSearchParams({ field, prefix, limit: '50' });
        const response = await fetch(`/api/properties/distinct?${params.toString()}`, { signal: controller.signal });
        if (!response.ok) return;
        const data = await response.json();
        if (controller.signal.aborted) return;
        setter(Array.isArray(data.values) ? data.values : []);
      } catch (error: any) {
        if (error?.name !== 'AbortError') console.error(`Error fetching ${field} suggestions:`, error);
      }
    };
    fetchDistinctValues('tenant', tenantFilter.trim(), setTenantSuggestions);
    fetchDistinctValues('city', cityFilter.trim(), setCitySuggestions);
    fetchDistinctValues('state', stateFilter.trim(), setStateSuggestions);
    return () => controller.abort();
  }, [isFilterOpen, tenantFilter, cityFilter, stateFilter, properties, useServerFiltering]);

  useEffect(() => {
    const visibleIds = new Set(filteredPropertyIds);
    setSelectedIds((prev) => prev.filter((id) => visibleIds.has(id)));
  }, [filteredPropertyIds]);

  useEffect(() => {
    if (!selectedProperty) {
      setIsNotesEditing(false);
      setNotesDraft('');
      setIsOwnerContactsEditing(false);
      setOwnerContactsDraft([]);
      setBrokerNoteDraft('');
      setBrokerContactDraft(createEmptyOwnerContact());
      return;
    }
    setNotesDraft(typeof selectedProperty.notes === 'string' ? selectedProperty.notes : '');
    setOwnerContactsDraft(normalizeOwnerContacts(selectedProperty.ownerContacts));
    setIsNotesEditing(false);
    setIsOwnerContactsEditing(false);
    setBrokerNoteDraft('');
    setBrokerContactDraft(createEmptyOwnerContact());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty?.id]);

  useEffect(() => {
    if (!isFilterOpen) return;
    const handlePointerDownOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (filterControlsRef.current && !filterControlsRef.current.contains(target)) setIsFilterOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDownOutside);
    return () => document.removeEventListener('mousedown', handlePointerDownOutside);
  }, [isFilterOpen]);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const openPropertyDetails = async (property: Property) => {
    if (readOnly) {
      setSelectedProperty(normalizePropertyForEditing(property));
      return;
    }
    try {
      const response = await fetch(`/api/properties/${property.id}`);
      if (!response.ok) { setSelectedProperty(normalizePropertyForEditing(property)); return; }
      const fullProperty = (await response.json()) as Property;
      setSelectedProperty(normalizePropertyForEditing(fullProperty));
    } catch {
      setSelectedProperty(normalizePropertyForEditing(property));
    }
  };

  const startEditing = () => {
    if (!selectedProperty) return;
    setIsNotesEditing(false);
    setIsOwnerContactsEditing(false);
    setDraft(normalizePropertyForEditing(JSON.parse(JSON.stringify(selectedProperty))));
    setIsEditing(true);
  };

  const cancelEditing = () => { setIsEditing(false); setDraft(null); };

  const saveEditing = async () => {
    if (!draft) return;
    setIsSaving(true);
    try {
      const resp = await fetch(`/api/properties/${draft.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error(err.error || 'Failed to update property'); }
      const updated = normalizePropertyForEditing((await resp.json()) as Property);
      handleUpdateProperty(updated);
      setSelectedProperty(updated);
      setIsEditing(false);
      setDraft(null);
    } catch (e: any) {
      alert(`Error saving property: ${e.message || String(e)}`);
    } finally {
      setIsSaving(false);
    }
  };

  const refreshFromReportAll = async () => {
    if (!selectedProperty) return;
    if (isEditing) { alert('Save or cancel your edits before refreshing from ReportAll.'); return; }
    setIsSaving(true);
    try {
      const resp = await fetch(`/api/properties/${selectedProperty.id}/reportall`, { method: 'POST' });
      if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error(err.error || 'Failed to refresh from ReportAll'); }
      const updated = (await resp.json()) as Property;
      handleUpdateProperty(updated);
      setSelectedProperty(updated);
    } catch (e: any) {
      alert(`Error refreshing ReportAll: ${e.message || String(e)}`);
    } finally {
      setIsSaving(false);
    }
  };

  const refreshReportAllForFiltered = async () => {
    if (filteredTotal === 0) return;
    if (!window.confirm(`This will refresh ReportAll data for ${filteredTotal} filtered properties. Continue?`)) return;
    if (!window.confirm(`Are you sure? This will make ${filteredTotal} API calls to ReportAll.`)) return;

    setIsRefreshingFilteredReportAll(true);
    try {
      const resp = await fetch('/api/properties/reportall/refresh-filtered', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          q: searchTerm.trim(),
          tenant: tenantFilter.trim(),
          city: cityFilter.trim(),
          state: stateFilter.trim(),
          starred: starredFilter,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to refresh from ReportAll');
      }
      const data = await resp.json();
      alert(data.message || 'Done');
      if (Array.isArray(data.updatedProperties)) {
        const updatedMap = new Map<string, Property>(data.updatedProperties.map((p: Property) => [p.id, p]));
        setFilteredProperties((prev) =>
          prev.map((p) => (updatedMap.has(p.id) ? { ...p, ...updatedMap.get(p.id) } : p)) as Property[]
        );
      }
    } catch (e: any) {
      alert(`Error: ${e.message || String(e)}`);
    } finally {
      setIsRefreshingFilteredReportAll(false);
    }
  };

  const toggleSelectProperty = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((cur) => cur !== id) : [...prev, id]));
  };

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) { setSelectedIds([]); return; }
    setSelectedIds(filteredPropertyIds);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    const count = selectedIds.length;
    if (!window.confirm(`Delete ${count} selected propert${count === 1 ? 'y' : 'ies'}?`)) return;
    setIsSaving(true);
    try {
      await handleDeleteProperties(selectedIds);
      if (selectedProperty && selectedIds.includes(selectedProperty.id)) setSelectedProperty(null);
      setSelectedIds([]);
    } finally {
      setIsSaving(false);
    }
  };

  const savePropertyPatch = async (patch: Partial<Property>) => {
    if (!selectedProperty) return;
    setIsSaving(true);
    try {
      const payload = { ...selectedProperty, ...patch };
      const resp = await fetch(`/api/properties/${selectedProperty.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error(err.error || 'Failed to update property'); }
      const updated = normalizePropertyForEditing((await resp.json()) as Property);
      handleUpdateProperty(updated);
      setSelectedProperty(updated);
      return updated;
    } finally {
      setIsSaving(false);
    }
  };

  const hasBrokerContactDraftData = () => {
    return !!(
      brokerContactDraft.firstName?.trim() ||
      brokerContactDraft.lastName?.trim() ||
      brokerContactDraft.cell?.trim() ||
      brokerContactDraft.email?.trim() ||
      brokerContactDraft.address?.trim()
    );
  };

  const submitBrokerAddition = async () => {
    if (!selectedProperty || !brokerIdentity) return;
    if (!brokerNoteDraft.trim() && !hasBrokerContactDraftData()) {
      alert('Please add a note or contact information.');
      return;
    }
    setIsSaving(true);
    try {
      const resp = await fetch(`/api/properties/${selectedProperty.id}/broker-additions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brokerId: brokerIdentity.id,
          brokerName: brokerIdentity.name,
          note: brokerNoteDraft.trim(),
          ownerContact: hasBrokerContactDraftData() ? brokerContactDraft : null,
        }),
      });
      if (!resp.ok) throw new Error('Failed to add data');
      const updated = (await resp.json()) as Property;
      handleUpdateProperty(updated);
      setSelectedProperty(normalizePropertyForEditing(updated));
      setFilteredProperties((prev) => prev.map((p) => (p.id === updated.id ? { ...p, brokerAdditions: updated.brokerAdditions } : p)));
      setBrokerNoteDraft('');
      setBrokerContactDraft(createEmptyOwnerContact());
    } catch (e: any) {
      alert(`Error: ${e.message || String(e)}`);
    } finally {
      setIsSaving(false);
    }
  };

  const startNotesEditing = () => {
    if (!selectedProperty || isEditing) return;
    setNotesDraft(typeof selectedProperty.notes === 'string' ? selectedProperty.notes : '');
    setIsNotesEditing(true);
  };

  const cancelNotesEditing = () => {
    if (!selectedProperty) return;
    setNotesDraft(typeof selectedProperty.notes === 'string' ? selectedProperty.notes : '');
    setIsNotesEditing(false);
  };

  const saveNotesEditing = async () => {
    if (!selectedProperty) return;
    try { await savePropertyPatch({ notes: notesDraft }); setIsNotesEditing(false); } catch (e: any) { alert(`Error saving notes: ${e.message || String(e)}`); }
  };

  const startOwnerContactsEditing = () => {
    if (!selectedProperty || isEditing) return;
    setOwnerContactsDraft(normalizeOwnerContacts(selectedProperty.ownerContacts));
    setIsOwnerContactsEditing(true);
  };

  const cancelOwnerContactsEditing = () => {
    if (!selectedProperty) return;
    setOwnerContactsDraft(normalizeOwnerContacts(selectedProperty.ownerContacts));
    setIsOwnerContactsEditing(false);
  };

  const saveOwnerContactsEditing = async () => {
    if (!selectedProperty) return;
    try { await savePropertyPatch({ ownerContacts: ownerContactsDraft }); setIsOwnerContactsEditing(false); } catch (e: any) { alert(`Error saving owner contacts: ${e.message || String(e)}`); }
  };

  const updateDraftNotes = (value: string) => {
    if (isEditing) { if (!draft) return; setDraft({ ...draft, notes: value }); return; }
    if (isNotesEditing) setNotesDraft(value);
  };

  const updateEditableOwnerContacts = (updater: (current: OwnerContact[]) => OwnerContact[]) => {
    if (isEditing) {
      if (!draft) return;
      const next = updater(normalizeOwnerContacts(draft.ownerContacts));
      setDraft({ ...draft, ownerContacts: next });
      return;
    }
    if (isOwnerContactsEditing) setOwnerContactsDraft((prev) => updater(normalizeOwnerContacts(prev)));
  };

  const updateOwnerContactField = (contactIndex: number, field: keyof Omit<OwnerContact, 'additionalPhones'>, value: string) => {
    updateEditableOwnerContacts((contacts) => {
      if (!contacts[contactIndex]) return contacts;
      const nextValue = field === 'cell' ? formatUsPhone(value) : value;
      const next = [...contacts];
      next[contactIndex] = { ...next[contactIndex], [field]: nextValue };
      return next;
    });
  };

  const addOwnerContact = () => updateEditableOwnerContacts((contacts) => [...contacts, createEmptyOwnerContact()]);

  const handleNewContact = () => {
    if (!selectedProperty) return;
    if (!isEditing) {
      if (!isOwnerContactsEditing) {
        setOwnerContactsDraft(normalizeOwnerContacts(selectedProperty.ownerContacts));
        setIsOwnerContactsEditing(true);
      }
      setOwnerContactsDraft((prev) => [...normalizeOwnerContacts(prev), createEmptyOwnerContact()]);
      return;
    }
    addOwnerContact();
  };

  const removeOwnerContact = (contactIndex: number) => updateEditableOwnerContacts((contacts) => contacts.filter((_, idx) => idx !== contactIndex));

  const updateAdditionalPhone = (contactIndex: number, phoneIndex: number, value: string) => {
    updateEditableOwnerContacts((contacts) => {
      const contact = contacts[contactIndex];
      if (!contact) return contacts;
      const additionalPhones = Array.isArray(contact.additionalPhones) ? [...contact.additionalPhones] : [''];
      if (!additionalPhones[phoneIndex] && additionalPhones[phoneIndex] !== '') return contacts;
      additionalPhones[phoneIndex] = formatUsPhone(value);
      const next = [...contacts];
      next[contactIndex] = { ...contact, additionalPhones };
      return next;
    });
  };

  const addAdditionalPhone = (contactIndex: number) => {
    updateEditableOwnerContacts((contacts) => {
      const contact = contacts[contactIndex];
      if (!contact) return contacts;
      const additionalPhones = Array.isArray(contact.additionalPhones) ? [...contact.additionalPhones] : [];
      additionalPhones.push('');
      const next = [...contacts];
      next[contactIndex] = { ...contact, additionalPhones };
      return next;
    });
  };

  const removeAdditionalPhone = (contactIndex: number, phoneIndex: number) => {
    updateEditableOwnerContacts((contacts) => {
      const contact = contacts[contactIndex];
      if (!contact) return contacts;
      const additionalPhones = (Array.isArray(contact.additionalPhones) ? contact.additionalPhones : ['']).filter((_, idx) => idx !== phoneIndex);
      const next = [...contacts];
      next[contactIndex] = { ...contact, additionalPhones: additionalPhones.length > 0 ? additionalPhones : [''] };
      return next;
    });
  };

  const SECTION_DEFS: SectionDef[] = [
    { id: 'core', title: 'Core', openByDefault: true, fields: [{ path: 'tenant', label: 'Tenant' }, { path: 'title', label: 'Title' }, { path: 'phone', label: 'Phone' }] },
    { id: 'address', title: 'Address', openByDefault: true, fields: [{ path: 'fullAddress', label: 'Full Address', multiline: true }, { path: 'address', label: 'Address' }, { path: 'city', label: 'City' }, { path: 'state', label: 'State' }, { path: 'zipcode', label: 'Zipcode' }] },
    { id: 'ownership', title: 'Ownership & Mailing', openByDefault: true, fields: [{ path: 'entityOwnership', label: 'Owner (stored)' }, { path: 'mailingAddress', label: 'Mailing Address (stored)', multiline: true }, { path: 'reportAllParcel.owner', label: 'Owner (ReportAll)' }, { path: 'reportAllParcel.mail_address1', label: 'Mail Address 1' }, { path: 'reportAllParcel.mail_address2', label: 'Mail Address 2' }, { path: 'reportAllParcel.mail_address3', label: 'Mail Address 3' }, { path: 'reportAllParcel.mail_recipient', label: 'Mail Recipient' }, { path: 'reportAllParcel.mail_placename', label: 'Mail City' }, { path: 'reportAllParcel.mail_statename', label: 'Mail State' }, { path: 'reportAllParcel.mail_zipcode', label: 'Mail Zip' }, { path: 'reportAllParcel.mail_countryname', label: 'Mail Country' }] },
    { id: 'identification', title: 'Identification & Jurisdiction', fields: [{ path: 'reportAllParcel.parcel_id', label: 'Parcel ID / APN' }, { path: 'reportAllParcel.robust_id', label: 'Robust ID' }, { path: 'reportAllParcel.alt_id_1', label: 'Alt ID 1' }, { path: 'reportAllParcel.alt_id_2', label: 'Alt ID 2' }, { path: 'reportAllParcel.county_name', label: 'County' }, { path: 'reportAllParcel.county_id', label: 'County ID' }, { path: 'reportAllParcel.state_abbr', label: 'State Abbr' }, { path: 'reportAllParcel.muni_name', label: 'Municipality' }, { path: 'reportAllParcel.county_link', label: 'County Link', multiline: true }] },
    { id: 'values', title: 'Market Values', openByDefault: true, fields: [{ path: 'reportAllParcel.mkt_val_land', label: 'Market Land Value' }, { path: 'reportAllParcel.mkt_val_bldg', label: 'Market Improvement Value' }, { path: 'reportAllParcel.mkt_val_tot', label: 'Market Total Value' }] },
    { id: 'building', title: 'Building', openByDefault: true, fields: [{ path: 'reportAllParcel.bldg_sqft', label: 'Building Sqft' }, { path: 'reportAllParcel.year_built', label: 'Year Built' }, { path: 'reportAllParcel.eff_year_built', label: 'Effective Year Built' }, { path: 'reportAllParcel.story_height', label: 'Story Height / Stories' }, { path: 'reportAllParcel.buildings', label: 'Number of Buildings' }, { path: 'reportAllParcel.style', label: 'Style' }, { path: 'reportAllParcel.exterior', label: 'Exterior' }, { path: 'reportAllParcel.condition', label: 'Condition / Quality' }, { path: 'reportAllParcel.heatsrc', label: 'Heat Source' }, { path: 'reportAllParcel.cooling', label: 'Cooling' }, { path: 'reportAllParcel.total_rooms', label: 'Total Rooms' }, { path: 'reportAllParcel.bedrooms', label: 'Bedrooms' }, { path: 'reportAllParcel.fullbath', label: 'Full Baths' }, { path: 'reportAllParcel.halfbath', label: 'Half Baths' }, { path: 'reportAllParcel.total_bath', label: 'Total Baths' }, { path: 'reportAllParcel.water', label: 'Water' }, { path: 'reportAllParcel.sewer', label: 'Sewer' }] },
    { id: 'land', title: 'Land & Classification', fields: [{ path: 'reportAllParcel.land_use_code', label: 'Land Use Code' }, { path: 'reportAllParcel.land_use_class', label: 'Land Use Class' }, { path: 'reportAllParcel.ngh_code', label: 'Neighborhood Code' }, { path: 'reportAllParcel.acreage', label: 'Acreage (source)' }, { path: 'reportAllParcel.calc_acreage', label: 'Acreage (calculated)' }, { path: 'reportAllParcel.acreage_adjacent_with_sameowner', label: 'Adj. Acreage (same owner)' }, { path: 'reportAllParcel.zoning', label: 'Zoning' }, { path: 'reportAllParcel.owner_occupied', label: 'Owner Occupied' }, { path: 'reportAllParcel.usps_residential', label: 'USPS Residential/Commercial' }] },
    { id: 'sales', title: 'Sales / Transfer', fields: [{ path: 'reportAllParcel.trans_date', label: 'Transfer Date' }, { path: 'reportAllParcel.sale_price', label: 'Sale Price' }] },
    { id: 'legal', title: 'Legal / PLSS', fields: [{ path: 'reportAllParcel.legal_desc1', label: 'Legal Description 1', multiline: true }, { path: 'reportAllParcel.legal_desc2', label: 'Legal Description 2', multiline: true }, { path: 'reportAllParcel.legal_desc3', label: 'Legal Description 3', multiline: true }, { path: 'reportAllParcel.section_township_range', label: 'Section-Township-Range' }] },
    { id: 'geo', title: 'Geography / Census', fields: [{ path: 'reportAllParcel.latitude', label: 'Latitude' }, { path: 'reportAllParcel.longitude', label: 'Longitude' }, { path: 'reportAllParcel.elevation', label: 'Elevation' }, { path: 'reportAllParcel.census_place', label: 'Census Place' }, { path: 'reportAllParcel.census_tract', label: 'Census Tract' }, { path: 'reportAllParcel.census_block', label: 'Census Block' }, { path: 'reportAllParcel.zip_code', label: 'Census Zip' }] },
    { id: 'environment', title: 'Environmental / Overlays', fields: [{ path: 'reportAllParcel.land_cover', label: 'Land Cover', multiline: true }, { path: 'reportAllParcel.crop_cover', label: 'Crop Cover', multiline: true }, { path: 'reportAllParcel.fld_zone', label: 'Flood Zone' }, { path: 'reportAllParcel.zone_subty', label: 'Flood Zone Subtype' }] },
    { id: 'geometry', title: 'Geometry', fields: [{ path: 'reportAllParcel.geom_as_wkt', label: 'Geometry (WKT)', multiline: true }] },
    { id: 'metadata', title: 'Record Metadata', fields: [{ path: 'id', label: 'Property ID' }, { path: 'createdAt', label: 'Created' }, { path: 'reportAllParcel.last_updated', label: 'ReportAll Last Updated' }] },
  ];

  const renderFieldValue = (obj: any, field: FieldDef) => {
    const value = getValueByPath(obj, field.path);
    if (isEditing && !readOnly) {
      const isJson = isLikelyJsonText(value);
      const inputProps = {
        value,
        onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          if (!draft) return;
          const next = JSON.parse(JSON.stringify(draft));
          setValueByPath(next, field.path, e.target.value);
          setDraft(next);
        },
      };
      return field.multiline || isJson ? (
        <textarea className="prop-field-input prop-field-textarea" rows={4} {...inputProps} />
      ) : (
        <input className="prop-field-input" type="text" {...inputProps} />
      );
    }

    const shouldFormatMarketValue = field.path.startsWith('reportAllParcel.mkt_val_');
    const displayValue = shouldFormatMarketValue ? formatMarketValue(value) : value;

    const isAddressField = ['fullAddress', 'address'].includes(field.path);
    if (isAddressField && displayValue && displayValue !== 'N/A' && selectedProperty) {
      return (
        <a href="#" className="address-link" onClick={(e) => { e.preventDefault(); navigateToPropertyOnMap(selectedProperty); }}>
          {displayValue}
        </a>
      );
    }

    if (field.multiline || displayValue.length > 120) return <pre className="prop-field-pre">{displayValue || 'N/A'}</pre>;
    return <span className="prop-field-text">{displayValue || 'N/A'}</span>;
  };

  const renderNotesSection = (obj: Property) => {
    const isSectionEditing = isEditing || isNotesEditing;
    const notes = isEditing
      ? (typeof draft?.notes === 'string' ? draft.notes : '')
      : (isNotesEditing ? notesDraft : (typeof obj.notes === 'string' ? obj.notes : ''));
    return (
      <details className="prop-section" open>
        <summary className="prop-section-summary"><span className="prop-section-title">Notes</span></summary>
        <div className="prop-section-body">
          {!isEditing && !readOnly && (
            <div className="section-edit-actions">
              {!isNotesEditing ? (
                <button type="button" className="owner-contact-btn" onClick={startNotesEditing} disabled={isSaving}>Edit Notes</button>
              ) : (
                <>
                  <button type="button" className="owner-contact-btn" onClick={saveNotesEditing} disabled={isSaving}>{isSaving ? 'Saving…' : 'Save Notes'}</button>
                  <button type="button" className="owner-contact-btn owner-contact-btn-danger" onClick={cancelNotesEditing} disabled={isSaving}>Cancel</button>
                </>
              )}
            </div>
          )}
          {isSectionEditing && !readOnly ? (
            <textarea className="prop-field-input prop-field-textarea" rows={6} value={notes} onChange={(e) => updateDraftNotes(e.target.value)} placeholder="Add notes..." />
          ) : (
            <pre className="prop-field-pre">{notes || 'N/A'}</pre>
          )}
        </div>
      </details>
    );
  };

  const renderOwnerContactInfoSection = (obj: Property) => {
    const isSectionEditing = isEditing || isOwnerContactsEditing;
    const contacts = isEditing
      ? normalizeOwnerContacts(draft?.ownerContacts)
      : (isOwnerContactsEditing ? normalizeOwnerContacts(ownerContactsDraft) : normalizeOwnerContacts(obj.ownerContacts));

    return (
      <details className="prop-section" open>
        <summary className="prop-section-summary"><span className="prop-section-title">Owner Contact Information</span></summary>
        <div className="prop-section-body">
          {!isEditing && !readOnly && (
            <div className="section-edit-actions">
              {!isOwnerContactsEditing ? (
                <button type="button" className="owner-contact-btn" onClick={startOwnerContactsEditing} disabled={isSaving}>Edit Owner Contacts</button>
              ) : (
                <>
                  <button type="button" className="owner-contact-btn" onClick={saveOwnerContactsEditing} disabled={isSaving}>{isSaving ? 'Saving…' : 'Save Contacts'}</button>
                  <button type="button" className="owner-contact-btn owner-contact-btn-danger" onClick={cancelOwnerContactsEditing} disabled={isSaving}>Cancel</button>
                </>
              )}
            </div>
          )}
          {contacts.length === 0 && !isSectionEditing ? (
            <span className="prop-field-text">N/A</span>
          ) : (
            <div className="owner-contact-list">
              {contacts.map((contact, contactIndex) => (
                <div key={`owner-contact-${contactIndex}`} className="owner-contact-card">
                  <div className="owner-contact-card-header">
                    <h4>Owner Contact #{contactIndex + 1}</h4>
                    {isSectionEditing && !readOnly && (
                      <button type="button" className="owner-contact-btn owner-contact-btn-danger" onClick={() => removeOwnerContact(contactIndex)}>Remove Contact</button>
                    )}
                  </div>
                  <div className="prop-fields-grid">
                    {([{ label: 'First Name', key: 'firstName' as const }, { label: 'Last Name', key: 'lastName' as const }, { label: 'Age', key: 'age' as const }, { label: 'Cell', key: 'cell' as const }, { label: 'Email', key: 'email' as const }]).map((field) => (
                      <div key={field.key} className="prop-field">
                        <label className="prop-field-label">{field.label}</label>
                        {isSectionEditing && !readOnly ? (
                          <input className="prop-field-input" type="text" value={String(contact[field.key] ?? '')} onChange={(e) => updateOwnerContactField(contactIndex, field.key, e.target.value)} placeholder={field.key === 'cell' ? '(###) ###-####' : undefined} />
                        ) : (
                          <span className="prop-field-text">{field.key === 'cell' ? (formatUsPhone(String(contact[field.key] ?? '')) || 'N/A') : (String(contact[field.key] ?? '').trim() || 'N/A')}</span>
                        )}
                      </div>
                    ))}
                    <div className="prop-field owner-contact-address">
                      <label className="prop-field-label">Address</label>
                      {isSectionEditing && !readOnly ? (
                        <textarea className="prop-field-input prop-field-textarea" rows={3} value={String(contact.address ?? '')} onChange={(e) => updateOwnerContactField(contactIndex, 'address', e.target.value)} />
                      ) : (
                        <pre className="prop-field-pre">{String(contact.address ?? '').trim() || 'N/A'}</pre>
                      )}
                    </div>
                  </div>
                  <div className="owner-additional-phones">
                    <label className="prop-field-label">Additional Phone Numbers</label>
                    {(Array.isArray(contact.additionalPhones) ? contact.additionalPhones : ['']).map((phone, phoneIndex) => (
                      <div key={`owner-${contactIndex}-phone-${phoneIndex}`} className="owner-phone-row">
                        {isSectionEditing && !readOnly ? (
                          <>
                            <input className="prop-field-input" type="text" value={String(phone ?? '')} onChange={(e) => updateAdditionalPhone(contactIndex, phoneIndex, e.target.value)} placeholder="(###) ###-####" />
                            <button type="button" className="owner-contact-btn owner-contact-btn-danger" onClick={() => removeAdditionalPhone(contactIndex, phoneIndex)}>Remove</button>
                          </>
                        ) : (
                          <span className="prop-field-text">{formatUsPhone(String(phone ?? '')) || 'N/A'}</span>
                        )}
                      </div>
                    ))}
                    {isSectionEditing && !readOnly && (
                      <button type="button" className="owner-contact-btn" onClick={() => addAdditionalPhone(contactIndex)}>Add Phone</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {isSectionEditing && !readOnly && (
            <button type="button" className="owner-contact-btn add-owner-contact-btn" onClick={handleNewContact}>New Contact</button>
          )}
        </div>
      </details>
    );
  };

  const renderBrokerAdditionSection = () => {
    if (!readOnly || !brokerIdentity || !selectedProperty) return null;
    return (
      <details className="prop-section" open>
        <summary className="prop-section-summary"><span className="prop-section-title">Add Data (Append Only)</span></summary>
        <div className="prop-section-body">
          <p className="prop-note" style={{ marginBottom: '0.75rem' }}>Your additions are appended and cannot overwrite existing values.</p>
          <div className="prop-fields-grid">
            <div className="prop-field" style={{ gridColumn: '1 / -1' }}>
              <label className="prop-field-label">Add Note</label>
              <textarea className="prop-field-input prop-field-textarea" rows={3} value={brokerNoteDraft} onChange={(e) => setBrokerNoteDraft(e.target.value)} placeholder="Enter a note..." />
            </div>
            <div className="prop-field">
              <label className="prop-field-label">Contact First Name</label>
              <input className="prop-field-input" type="text" value={brokerContactDraft.firstName ?? ''} onChange={(e) => setBrokerContactDraft((prev) => ({ ...prev, firstName: e.target.value }))} />
            </div>
            <div className="prop-field">
              <label className="prop-field-label">Contact Last Name</label>
              <input className="prop-field-input" type="text" value={brokerContactDraft.lastName ?? ''} onChange={(e) => setBrokerContactDraft((prev) => ({ ...prev, lastName: e.target.value }))} />
            </div>
            <div className="prop-field">
              <label className="prop-field-label">Contact Cell</label>
              <input className="prop-field-input" type="text" value={brokerContactDraft.cell ?? ''} onChange={(e) => setBrokerContactDraft((prev) => ({ ...prev, cell: formatUsPhone(e.target.value) }))} placeholder="(###) ###-####" />
            </div>
            <div className="prop-field">
              <label className="prop-field-label">Contact Email</label>
              <input className="prop-field-input" type="text" value={brokerContactDraft.email ?? ''} onChange={(e) => setBrokerContactDraft((prev) => ({ ...prev, email: e.target.value }))} />
            </div>
            <div className="prop-field" style={{ gridColumn: '1 / -1' }}>
              <label className="prop-field-label">Contact Address</label>
              <textarea className="prop-field-input prop-field-textarea" rows={2} value={brokerContactDraft.address ?? ''} onChange={(e) => setBrokerContactDraft((prev) => ({ ...prev, address: e.target.value }))} />
            </div>
          </div>
          <button type="button" className="save-btn" style={{ marginTop: '0.75rem' }} onClick={submitBrokerAddition} disabled={isSaving}>
            {isSaving ? 'Adding…' : 'Add Data'}
          </button>
        </div>
      </details>
    );
  };

  const navigateToPropertyOnMap = (property: Property) => {
    setFocusMapPropertyId(property.id);
    setViewMode('map');
  };

  const renderAddressContent = (property: Property) => {
    const addr = getDisplayAddress(property);
    if (addr !== 'N/A') {
      return (
        <a
          href="#"
          className="address-link"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigateToPropertyOnMap(property); }}
        >
          {addr}
        </a>
      );
    }
    return addr;
  };

  if (selectedProperty) {
    const viewObj = (isEditing ? draft : selectedProperty) || selectedProperty;
    return (
      <div className={`properties-container ${readOnly ? 'properties-container-readonly' : ''}`}>
        <div className="properties-header">
          <button className="back-button" onClick={() => { setSelectedProperty(null); setIsEditing(false); setDraft(null); }}>
            ← Back to Properties
          </button>
        </div>
        <div className="property-detail">
          <div className="property-detail-header">
            <div className="property-detail-header-left">
              <h2>Property Details</h2>
              <p className="property-detail-subtitle">{getPrimaryLabel(selectedProperty)}</p>
            </div>
            {!readOnly && (
              <div className="property-detail-actions">
                <button className="star-btn" onClick={() => toggleStarProperty(selectedProperty)} title={isStarredProperty(selectedProperty) ? 'Unstar' : 'Star'}>
                  {isStarredProperty(selectedProperty) ? '★' : '☆'}
                </button>
                {!isEditing ? (
                  <>
                    <button className="edit-btn" onClick={startEditing} disabled={isSaving}>Edit</button>
                    <button className="cancel-btn" onClick={refreshFromReportAll} disabled={isSaving}>Refresh from ReportAll</button>
                  </>
                ) : (
                  <>
                    <button className="save-btn" onClick={saveEditing} disabled={isSaving}>{isSaving ? 'Saving…' : 'Save'}</button>
                    <button className="cancel-btn" onClick={cancelEditing} disabled={isSaving}>Cancel</button>
                  </>
                )}
                <button className="delete-btn" onClick={async () => { await handleDeleteProperty(selectedProperty.id); setSelectedProperty(null); }} disabled={isSaving}>
                  Delete Property
                </button>
              </div>
            )}
          </div>
          <div className="property-detail-content">
            <div className="prop-sections">
              {SECTION_DEFS.map((section) => (
                <details key={section.id} className="prop-section" open={!!section.openByDefault}>
                  <summary className="prop-section-summary"><span className="prop-section-title">{section.title}</span></summary>
                  <div className="prop-section-body">
                    <div className="prop-fields-grid">
                      {section.fields.map((field) => (
                        <div key={field.path} className="prop-field">
                          <label className="prop-field-label">{field.label}</label>
                          <div className="prop-field-value">{renderFieldValue(viewObj, field)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </details>
              ))}
              {renderBrokerAdditionSection()}
              {renderNotesSection(viewObj as Property)}
              {renderOwnerContactInfoSection(viewObj as Property)}
              <div className="prop-note">
                <span>Note: Most `reportAllParcel.*` fields are county-dependent; blanks are normal.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`properties-container ${readOnly ? 'properties-container-readonly' : ''}`}>
      <div className="properties-header">
        <div className="header-left">
          <h1>Properties</h1>
          <span className="property-count">
            {filteredTotal} {filteredTotal === 1 ? 'property' : 'properties'}
            {filteredTotal > 0 ? ` • Showing ${pageStart}-${pageEnd}` : ''}
          </span>
        </div>
        <div className="header-right">
          {!readOnly && (
            <div className="bulk-actions">
              <button className="bulk-btn" onClick={toggleSelectAllFiltered} disabled={filteredPropertyIds.length === 0}>
                {allFilteredSelected ? 'Clear All' : 'Select All'}
              </button>
              <button className="bulk-delete-btn" onClick={handleBulkDelete} disabled={selectedIds.length === 0 || isSaving} title={selectedIds.length === 0 ? 'Select properties first' : `Delete ${selectedIds.length} selected`}>
                Delete Selected ({selectedIds.length})
              </button>
              <button className="bulk-btn" onClick={refreshReportAllForFiltered} disabled={filteredTotal === 0 || isRefreshingFilteredReportAll} title={filteredTotal === 0 ? 'No filtered properties to refresh' : `Refresh ReportAll for ${filteredTotal} filtered`}>
                {isRefreshingFilteredReportAll ? 'Refreshing from ReportAll…' : `Refresh from ReportAll (${filteredTotal})`}
              </button>
            </div>
          )}
          <div className="search-box">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input type="text" placeholder="Search properties..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
          <div className="filter-controls" ref={filterControlsRef}>
            <button className={`filter-btn ${isFilterOpen ? 'active' : ''}`} onClick={() => setIsFilterOpen((prev) => !prev)} title="Filter by Tenant, City, State, Starred">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              <span>Filter</span>
              {activeFilterCount > 0 && <span className="filter-badge">{activeFilterCount}</span>}
            </button>
            {isFilterOpen && (
              <div className="filter-popover">
                <label className="filter-field">
                  <span>Tenant</span>
                  <input type="text" placeholder="Filter tenant..." value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)} list="tenant-filter-options" />
                  <datalist id="tenant-filter-options">{tenantSuggestions.map((v) => <option key={v} value={v} />)}</datalist>
                </label>
                <label className="filter-field">
                  <span>City</span>
                  <input type="text" placeholder="Filter city..." value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} list="city-filter-options" />
                  <datalist id="city-filter-options">{citySuggestions.map((v) => <option key={v} value={v} />)}</datalist>
                </label>
                <label className="filter-field">
                  <span>State</span>
                  <input type="text" placeholder="Filter state..." value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} list="state-filter-options" />
                  <datalist id="state-filter-options">{stateSuggestions.map((v) => <option key={v} value={v} />)}</datalist>
                </label>
                <label className="filter-field">
                  <span>Starred</span>
                  <select value={starredFilter} onChange={(e) => setStarredFilter(e.target.value as any)}>
                    <option value="all">All</option>
                    <option value="starred">Starred</option>
                    <option value="nonstarred">Non-Starred</option>
                  </select>
                </label>
                <button className="clear-filters-btn" onClick={() => { setTenantFilter(''); setCityFilter(''); setStateFilter(''); setStarredFilter('all'); }} disabled={activeFilterCount === 0}>
                  Clear filters
                </button>
              </div>
            )}
          </div>
          <div className="view-controls">
            <button className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')} title="Grid View">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>
            </button>
            <button className={`view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="List View">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
            </button>
            <button className={`view-btn ${viewMode === 'map' ? 'active' : ''}`} onClick={() => setViewMode('map')} title="Map View">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21 3 6" /><line x1="9" y1="3" x2="9" y2="18" /><line x1="15" y1="6" x2="15" y2="21" /></svg>
            </button>
          </div>
          {headerAction}
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="bulk-selection-note">{selectedIds.length} selected ({filteredSelectedCount} visible in current filter).</div>
      )}

      {filteredTotal === 0 ? (
        <div className="empty-state">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
          <h3>{searchTerm ? 'No properties found' : 'No properties yet'}</h3>
          <p>{searchTerm ? 'Try adjusting your search terms' : 'Import a CSV file to create properties'}</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="properties-grid">
          {sortedFilteredProperties.map((property) => (
            <div key={property.id} className="property-card" onClick={() => openPropertyDetails(property)}>
              <div className="property-card-header">
                {!readOnly && (
                  <>
                    <label className="select-checkbox-wrap" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.includes(property.id)} onChange={() => toggleSelectProperty(property.id)} />
                    </label>
                    <button className="star-btn-small" onClick={(e) => { e.stopPropagation(); toggleStarProperty(property); }} title={isStarredProperty(property) ? 'Unstar' : 'Star'}>
                      {isStarredProperty(property) ? '★' : '☆'}
                    </button>
                  </>
                )}
                <h3>{getPrimaryLabel(property)}</h3>
                {!readOnly && (
                  <button className="card-delete-btn" onClick={async (e) => { e.stopPropagation(); await handleDeleteProperty(property.id); }} title="Delete property">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="m19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                  </button>
                )}
              </div>
              <div className="property-card-body">
                <div className="property-info">
                  <span className="info-label">Phone</span>
                  <span className="info-value">{property.phone || 'N/A'}</span>
                </div>
                <div className="property-info">
                  <span className="info-label">Full Address</span>
                  <span className="info-value">{renderAddressContent(property)}</span>
                </div>
              </div>
              <div className="property-card-footer">
                <span className="property-date">Added {formatDate(property.createdAt)}</span>
              </div>
            </div>
          ))}
        </div>
      ) : viewMode === 'list' ? (
        <div className="properties-list">
          <table className={`properties-table ${readOnly ? 'properties-table-readonly' : ''}`}>
            <thead>
              <tr>
                {!readOnly && (
                  <th><input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAllFiltered} aria-label="Select all filtered properties" /></th>
                )}
                {!readOnly && <th></th>}
                <th>Tenant</th>
                <th>Address</th>
                <th>City</th>
                <th>State</th>
                <th className="sortable-th" onClick={() => handleListSort('owner')}>
                  Property Owner {listSortKey === 'owner' ? (listSortDirection === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th className="sortable-th" onClick={() => handleListSort('phone')}>
                  Phone {listSortKey === 'phone' ? (listSortDirection === 'asc' ? '↑' : '↓') : ''}
                </th>
                {!readOnly && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {sortedFilteredProperties.map((property) => (
                <tr key={property.id} onClick={() => openPropertyDetails(property)}>
                  {!readOnly && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.includes(property.id)} onChange={() => toggleSelectProperty(property.id)} aria-label={`Select ${getPrimaryLabel(property)}`} />
                    </td>
                  )}
                  {!readOnly && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <button className="star-btn-small" onClick={() => toggleStarProperty(property)} title={isStarredProperty(property) ? 'Unstar' : 'Star'}>
                        {isStarredProperty(property) ? '★' : '☆'}
                      </button>
                    </td>
                  )}
                  <td>{property.tenant || 'N/A'}</td>
                  <td className="address-cell">{renderAddressContent(property)}</td>
                  <td>{property.city || 'N/A'}</td>
                  <td>{property.state || 'N/A'}</td>
                  <td>{getDisplayPropertyOwner(property)}</td>
                  <td>{getDisplayPhone(property)}</td>
                  {!readOnly && (
                    <td>
                      <button className="table-action-btn" onClick={async (e) => { e.stopPropagation(); await handleDeleteProperty(property.id); }} title="Delete property">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="m19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <PropertyMapView properties={filteredProperties} onSelectProperty={openPropertyDetails} focusPropertyId={focusMapPropertyId} />
      )}

      {hasMoreFiltered && <div className="prop-note">Additional pages available.</div>}
      {totalPages > 1 && (
        <div className="pagination-controls">
          <button className="pagination-btn" onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))} disabled={currentPage <= 1}>Previous</button>
          <span className="pagination-status">Page {currentPage} of {totalPages}</span>
          <button className="pagination-btn" onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))} disabled={currentPage >= totalPages}>Next</button>
        </div>
      )}
    </div>
  );
};

export default Properties;
